import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProperty } from '../lib/api';
import { useSiteSettings, buildWhatsAppUrl } from '../lib/settings';
import Header from '../components/Header';
import { Footer } from '../components/MoreSections';
import PropertyCard from '../components/PropertyCard';
import InterestModal from '../components/InterestModal';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Home, ChevronRight, ChevronLeft, MapPin, Compass, Building, CheckCircle2, Share2, Heart,
  Ruler, Phone, Layers, BadgeCheck, Sparkles,
} from 'lucide-react';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { whatsapp_number } = useSiteSettings();
  const [property, setProperty] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [interestOpen, setInterestOpen] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    setLoading(true); setError(null); setActiveImg(0);
    window.scrollTo(0, 0);
    getProperty(id)
      .then((data) => { setProperty(data.property); setRelated(data.related || []); })
      .catch((e) => setError(e.response?.status === 404 ? 'Property not found' : 'Failed to load property'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 space-y-4">
          <Skeleton className="h-4 w-64" />
          <div className="grid lg:grid-cols-[1fr_400px] gap-6">
            <Skeleton className="aspect-video w-full rounded-2xl" />
            <div className="space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-24 text-center">
          <h1 className="text-2xl font-black text-gray-900 mb-2">{error || 'Something went wrong'}</h1>
          <p className="text-gray-500 mb-6">The property you're looking for could not be found.</p>
          <Button onClick={() => navigate('/listings')} className="bg-[#9a1b2f] hover:bg-[#7a1524]">Browse Listings</Button>
        </div>
      </div>
    );
  }

  const images = property.images && property.images.length ? property.images : [property.image];
  const typeMap = { resale: 'Resale Properties', rent: 'Rent Properties', resale_commercial: 'Resale Commercial', rent_commercial: 'Rent Commercial' };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: property.name, url }); return; } catch(_) {} }
    try { await navigator.clipboard.writeText(url); alert('Link copied to clipboard'); } catch(_) {}
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-[#9a1b2f] flex items-center gap-1"><Home className="w-3 h-3" /> Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/listings?type=${property.listing_type}`} className="hover:text-[#9a1b2f]">{typeMap[property.listing_type]}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-700 font-medium line-clamp-1">{property.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          {/* Left: Gallery + Details */}
          <div className="space-y-6">
            {/* Gallery */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="relative aspect-[16/10] bg-gray-100">
                <img src={images[activeImg]} alt={property.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <Badge className="bg-white/95 text-[#9a1b2f] hover:bg-white shadow-md font-semibold border-0">{property.status}</Badge>
                  <Badge className="bg-[#9a1b2f] text-white hover:bg-[#9a1b2f] shadow-md border-0 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Verified
                  </Badge>
                </div>
                <div className="absolute top-4 right-4 flex gap-2">
                  <button onClick={() => setLiked((l) => !l)} className="w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center">
                    <Heart className={`w-4 h-4 ${liked ? 'fill-[#9a1b2f] text-[#9a1b2f]' : 'text-gray-700'}`} />
                  </button>
                  <button onClick={share} className="w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center">
                    <Share2 className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                {images.length > 1 && (
                  <>
                    <button onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setActiveImg((i) => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs">{activeImg + 1} / {images.length}</div>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="p-3 flex gap-2 overflow-x-auto">
                  {images.map((im, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition ${i === activeImg ? 'border-[#9a1b2f]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                      <img src={im} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Overview */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl md:text-3xl font-black text-gray-900">{property.name}</h1>
                  <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                    <MapPin className="w-4 h-4" /> {property.location}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Badge variant="outline" className="capitalize">{property.type}</Badge>
                    <Badge variant="outline" className="flex items-center gap-1"><Compass className="w-3 h-3" />{property.facing}</Badge>
                    <Badge variant="outline" className="capitalize">{property.furnishing}</Badge>
                    <Badge className="bg-[#fef2f4] text-[#9a1b2f] hover:bg-[#fef2f4] border-0">{property.bhk}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  {property.old_price && <div className="text-sm text-gray-400 line-through">{property.old_price}</div>}
                  <div className="text-3xl font-black text-[#9a1b2f] leading-tight">{property.price}</div>
                  {property.negotiable && <div className="text-xs text-emerald-600 font-semibold mt-0.5">Negotiable</div>}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                {[
                  { icon: Ruler, label: 'Area', value: `${property.sqft} sqft` },
                  { icon: Layers, label: 'Floor', value: property.floor },
                  { icon: Compass, label: 'Facing', value: property.facing },
                  { icon: BadgeCheck, label: 'Status', value: property.status },
                ].map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </div>
                    <div className="font-bold text-gray-900 text-sm mt-1">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">About This Property</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{property.description}</p>
            </div>

            {/* Amenities */}
            {property.amenities?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#9a1b2f]" /> Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {property.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />{a}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sticky Contact */}
          <aside className="lg:sticky lg:top-24 h-fit space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full border border-gray-100 bg-gray-50 flex items-center justify-center p-2 overflow-hidden shrink-0">
                  <img src={property.builder_logo} alt={property.builder} className="w-full h-full object-contain" />
                </div>
                <div>
                  <div className="text-[10px] uppercase text-gray-400 font-bold tracking-widest">Developer</div>
                  <div className="font-bold text-gray-900">{property.builder}</div>
                </div>
              </div>
              <div className="mt-5 space-y-2">
                <Button className="w-full bg-[#9a1b2f] hover:bg-[#7a1524] h-11" onClick={() => setInterestOpen(true)}>
                  <Phone className="w-4 h-4 mr-2" /> I'm Interested
                </Button>
                <a href={buildWhatsAppUrl(whatsapp_number, `Hi, I'm interested in ${property.name} (${property.price})`)} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#25D366] hover:bg-[#20b955] h-11 mt-2">WhatsApp Inquiry</Button>
                </a>
              </div>
              <div className="text-[11px] text-gray-500 mt-3 text-center">Our expert will reach out within 24 hours</div>
            </div>

            <div className="bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white rounded-2xl p-5">
              <h4 className="font-bold text-lg">Home Inspection</h4>
              <p className="text-xs opacity-90 mt-1">Get professional inspection before you take possession.</p>
              <Link to="/#inspection"><Button className="mt-3 w-full bg-white text-[#9a1b2f] hover:bg-gray-50" size="sm">Book Inspection</Button></Link>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-5">Similar Properties</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          </div>
        )}
      </div>

      <Footer />
      <InterestModal open={interestOpen} onOpenChange={setInterestOpen} property={property} />
    </div>
  );
};

export default PropertyDetailPage;
